import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCalculusDetail, getCalculuses, createCalculus, updateCalculus, deleteCalculus, getWorkingCalculuses, printCalculus, sendAccountSMS, chargeOffCalculus, reverseChargeOffCalculus } from "../api";
import { useNotification } from "../../../contexts/NotificationContext";


export const useGetCalculuses = (search, status, page, pageSize, location, actType, enabled = true) => {
  const statusKey = Array.isArray(status)
    ? [...status].map(String).sort().join(",")
    : String(status ?? "");
  const locationKey = Array.isArray(location)
    ? [...location].map(String).sort().join(",")
    : String(location ?? "");
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['calculuses', search, statusKey, page, pageSize, locationKey, actType],
    queryFn: () => getCalculuses(search, status, page, pageSize, location, actType),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
  });

  return { data, isLoading, isSuccess, isError, error };
};


export const useGetWorkingCalculuses = () => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['calculuses-working'],
    queryFn: () => getWorkingCalculuses(),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return { data, isLoading, isSuccess, isError, error };
};


export const useGetCalculusDetail = (id, tab, tenantId, queryEnabled = true) => {
  const { data, isLoading, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: ['calculus-detail', id, tab, tenantId],
    queryFn: () => getCalculusDetail(id, tab, tenantId),
    enabled: !!id && queryEnabled,
    staleTime: 1000 * 60 * 5, // 5 dakika cache
    cacheTime: 1000 * 60 * 10, // 10 dakika cache'de tut
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Mount olduğunda refetch yap
  });

  return { data, isLoading, isSuccess, isError, error, isFetching };
};

export const useCreateCalculus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  return useMutation({
    mutationFn: (payload) => createCalculus(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calculuses'] });
      showNotification('success', 'Booking created.');
    },
    onError: (error) => {
      console.error('Error creating booking:', error);
      showNotification('danger', 'Failed to create booking');
    }
  });
};

export const useUpdateCalculus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  return useMutation({
    mutationFn: ({ id, data }) => updateCalculus(id, data),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['calculuses'] });
      await queryClient.invalidateQueries({ queryKey: ['calculus-detail', variables?.id] });
      showNotification('success', 'Booking updated.');
    },
    onError: (error) => {
      console.error('Error updating booking:', error);
      showNotification('danger', 'Failed to update booking');
    }
  });
};

export const useDeleteCalculus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  return useMutation({
    mutationFn: (id) => deleteCalculus(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calculuses'] });
      showNotification('success', 'Booking deleted.');
    },
    onError: (error) => {
      console.error('Error deleting booking:', error);
      showNotification('danger', 'Failed to delete booking');
    }
  });
};

export const usePrintCalculus = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, documents }) => printCalculus(id, documents),
    onSuccess: (response) => {
      showNotification('success', 'PDF loaded successfully!');
      return response;
    },
    onError: (error) => {
      console.error("Error printing calculus:", error);
      showNotification('danger', 'Error generating PDF. Please try again.');
    },
  });
};
export const useSendAccountSMS = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (calculusId) => sendAccountSMS(calculusId),
    onSuccess: (data) => {
      showNotification('success', 'SMS authorization sent successfully!');
    },
    onError: (error) => {
      console.error("Error sending SMS:", error);
      let errorMessage = 'Error sending SMS. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};

export const useChargeOffCalculus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  return useMutation({
    mutationFn: ({ calculusId, data }) => chargeOffCalculus(calculusId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calculuses'] });
      await queryClient.invalidateQueries({ queryKey: ['calculus-detail'] });
      showNotification('success', 'Account charged off.');
    },
    onError: (error) => {
      console.error('Error charging off account:', error);
      showNotification('danger', 'Failed to charge off account.');
    }
  });
};

export const useReverseChargeOffCalculus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  return useMutation({
    mutationFn: ({ calculusId, data }) => reverseChargeOffCalculus(calculusId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calculuses'] });
      await queryClient.invalidateQueries({ queryKey: ['calculus-detail'] });
      showNotification('success', 'Charge off reversal completed.');
    },
    onError: (error) => {
      console.error('Error reversing charge off:', error);
      const message = error?.response?.data?.error?.message || 'Failed to reverse charge off.';
      showNotification('danger', message);
    }
  });
};
